"use strict";
/**
 * Soccer Mode order buttons (MISC / EXT split top row, GOAL?, GOAL, RED CARD).
 * Button tables only — the two-stage wiring and order routing live in the
 * sport-generic ../sport/buy-buttons module, and the stepper/pin/hint
 * machinery in ../sport/pricing.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SOCCER_BUTTONS = void 0;
exports.refreshSoccerHints = refreshSoccerHints;
exports.setupSoccerBuyButtons = setupSoccerBuyButtons;
exports.setupSoccerPricingControls = setupSoccerPricingControls;
const ui_1 = require("../../../ui");
const buy_buttons_1 = require("../sport/buy-buttons");
const pricing_1 = require("../sport/pricing");
/**
 * The 8 main soccer buttons (MISC / GOAL? / GOAL / RED CARD, 2 sides each) in
 * sport-generic form. MISC has 0 slippage at every factor, so pinning sets it
 * to the raw low ask. EXT has no per-button manual price, so it is not listed
 * here. Keys are `sc_`-prefixed: prices share the map with the other sports'.
 */
exports.SOCCER_BUTTONS = [
    { key: "sc_misc_a", side: "yes", btnId: "sc-misc-a", hintId: "sc-misc-a-slip", stepperId: "sc-misc-a-stepper", incId: "sc-misc-a-inc", decId: "sc-misc-a-dec", setId: "sc-misc-a-set", getSlippage: () => 0 },
    { key: "sc_goal_maybe_a", side: "yes", btnId: "sc-goal-maybe-a", hintId: "sc-goal-maybe-a-hint", stepperId: "sc-goal-maybe-a-stepper", incId: "sc-goal-maybe-a-inc", decId: "sc-goal-maybe-a-dec", setId: "sc-goal-maybe-a-set", getSlippage: (f) => (0, ui_1.getSoccerSlippage)("goal_maybe", f) },
    { key: "sc_goal_a", side: "yes", btnId: "sc-goal-a", hintId: "sc-goal-a-hint", stepperId: "sc-goal-a-stepper", incId: "sc-goal-a-inc", decId: "sc-goal-a-dec", setId: "sc-goal-a-set", getSlippage: (f) => (0, ui_1.getSoccerSlippage)("goal", f) },
    { key: "sc_red_a", side: "yes", btnId: "sc-red-a", hintId: "sc-red-a-hint", stepperId: "sc-red-a-stepper", incId: "sc-red-a-inc", decId: "sc-red-a-dec", setId: "sc-red-a-set", getSlippage: (f) => (0, ui_1.getSoccerSlippage)("red_card", f) },
    { key: "sc_misc_b", side: "no", btnId: "sc-misc-b", hintId: "sc-misc-b-slip", stepperId: "sc-misc-b-stepper", incId: "sc-misc-b-inc", decId: "sc-misc-b-dec", setId: "sc-misc-b-set", getSlippage: () => 0 },
    { key: "sc_goal_maybe_b", side: "no", btnId: "sc-goal-maybe-b", hintId: "sc-goal-maybe-b-hint", stepperId: "sc-goal-maybe-b-stepper", incId: "sc-goal-maybe-b-inc", decId: "sc-goal-maybe-b-dec", setId: "sc-goal-maybe-b-set", getSlippage: (f) => (0, ui_1.getSoccerSlippage)("goal_maybe", f) },
    { key: "sc_goal_b", side: "no", btnId: "sc-goal-b", hintId: "sc-goal-b-hint", stepperId: "sc-goal-b-stepper", incId: "sc-goal-b-inc", decId: "sc-goal-b-dec", setId: "sc-goal-b-set", getSlippage: (f) => (0, ui_1.getSoccerSlippage)("goal", f) },
    { key: "sc_red_b", side: "no", btnId: "sc-red-b", hintId: "sc-red-b-hint", stepperId: "sc-red-b-stepper", incId: "sc-red-b-inc", decId: "sc-red-b-dec", setId: "sc-red-b-set", getSlippage: (f) => (0, ui_1.getSoccerSlippage)("red_card", f) },
];
/** Look up the sport-generic metadata for a main soccer button element. */
function mainButtonMeta(btn) {
    if (!btn)
        return undefined;
    return exports.SOCCER_BUTTONS.find((m) => m.btnId === btn.id);
}
/**
 * Refresh the hint/label on every main soccer button.
 * In combined mode shows manual prices (¢); otherwise shows slippage (%).
 */
function refreshSoccerHints() {
    (0, pricing_1.refreshSportButtonHints)(exports.SOCCER_BUTTONS);
}
/**
 * Wire up Soccer Mode buy buttons for both sides: EXT (additional-market
 * orders only) and MISC/GOAL?/GOAL/RED CARD (factor-scaled slippage — always
 * 0 for MISC — or per-button manual price in combined mode). Same
 * two-stage-confirm UX as standard order buttons.
 *
 * @param scButtons         - Map of soccer button DOM references
 * @param allButtons        - All standard order buttons (for mutual-exclusion reset)
 * @param sportButtons      - All sport-layout buttons (for mutual-exclusion reset)
 * @param armTimeoutId      - Shared arm-timeout ref
 * @param isConfirmRequired - Callback returning current two-stage-confirm state
 */
function setupSoccerBuyButtons(scButtons, allButtons, sportButtons, armTimeoutId, isConfirmRequired) {
    const buy = (btn, side, buttonId, getSlippage) => ({ kind: "buy", btn, side, buttonId, getSlippage, meta: mainButtonMeta(btn) });
    const specs = [
        buy(scButtons.miscA, "yes", "sc_misc_yes", () => 0),
        { kind: "additional-only", btn: scButtons.extA, side: "yes", buttonId: "sc_ext_yes" },
        buy(scButtons.goalMaybeA, "yes", "sc_goal_maybe_yes", (f) => (0, ui_1.getSoccerSlippage)("goal_maybe", f)),
        buy(scButtons.goalA, "yes", "sc_goal_yes", (f) => (0, ui_1.getSoccerSlippage)("goal", f)),
        buy(scButtons.redA, "yes", "sc_red_yes", (f) => (0, ui_1.getSoccerSlippage)("red_card", f)),
        buy(scButtons.miscB, "no", "sc_misc_no", () => 0),
        { kind: "additional-only", btn: scButtons.extB, side: "no", buttonId: "sc_ext_no" },
        buy(scButtons.goalMaybeB, "no", "sc_goal_maybe_no", (f) => (0, ui_1.getSoccerSlippage)("goal_maybe", f)),
        buy(scButtons.goalB, "no", "sc_goal_no", (f) => (0, ui_1.getSoccerSlippage)("goal", f)),
        buy(scButtons.redB, "no", "sc_red_no", (f) => (0, ui_1.getSoccerSlippage)("red_card", f)),
    ];
    (0, buy_buttons_1.setupSportBuyButtons)(specs, allButtons, sportButtons, armTimeoutId, isConfirmRequired);
}
/**
 * Wire up the soccer per-button price steppers and the pin (📌) button.
 * Idempotent with the other sports' setup — the shared price modal is only
 * wired once.
 */
function setupSoccerPricingControls() {
    (0, pricing_1.setupSportPriceSteppers)(exports.SOCCER_BUTTONS);
    (0, pricing_1.setupSportSetToAskButton)("soccer-set-to-ask-btn", exports.SOCCER_BUTTONS, refreshSoccerHints);
}
